/**
 * Firebase Auth Token Cache
 * 
 * Caches Firebase ID tokens to avoid repeated Firebase API calls.
 * Firebase getIdToken() can take 1-3 seconds per call, and making
 * multiple API calls means significant delays just for authentication!
 * 
 * This cache reduces that to ~1-3s for the first call, then < 10ms
 * for subsequent calls using the cached token.
 * 
 * Tokens are cached for 55 minutes (Firebase tokens are valid for 1 hour).
 * This ensures we refresh 5 minutes early to avoid edge cases.
 */

import { Auth, User } from 'firebase/auth';

class AuthTokenCache {
  private token: string | null = null;
  private tokenExpiry: number | null = null;
  private refreshPromise: Promise<string> | null = null;
  private lastRefresh: number | null = null;

  /**
   * Get auth token (from cache if valid, otherwise fetch new one)
   * @param auth - Firebase auth instance
   * @returns Promise<string> Firebase ID token
   */
  async getToken(auth: Auth): Promise<string> {
    // If we have a valid cached token, return it immediately
    if (this.token && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        // eslint-disable-next-line no-console
        console.log(
          '✅ AuthTokenCache: Using cached token (expires in',
          Math.round((this.tokenExpiry - Date.now()) / 1000),
          'seconds)'
        );
      }
      return this.token;
    }

    // If a refresh is already in progress, wait for it
    // This prevents multiple simultaneous token refresh calls
    if (this.refreshPromise) {
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        // eslint-disable-next-line no-console
        console.log('⏳ AuthTokenCache: Waiting for token refresh in progress...');
      }
      return await this.refreshPromise;
    }

    // Refresh token
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      // eslint-disable-next-line no-console
      console.log('🔄 AuthTokenCache: Fetching new token...');
    }
    const refreshStart = performance.now();

    this.refreshPromise = this._refreshToken(auth);

    try {
      const token = await this.refreshPromise;
      const refreshDuration = ((performance.now() - refreshStart) / 1000).toFixed(2);
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        // eslint-disable-next-line no-console
        console.log(`✅ AuthTokenCache: Token refreshed in ${refreshDuration}s`);
      }
      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Internal method to refresh token from Firebase
   * @param auth - Firebase auth instance
   * @returns Promise<string> Firebase ID token
   * @private
   */
  private async _refreshToken(auth: Auth): Promise<string> {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    // Import getIdToken dynamically to avoid issues with SSR
    const { getIdToken } = await import('firebase/auth');
    const firebaseUser: User = auth.currentUser;

    // Get token from Firebase
    // forceRefresh=false uses Firebase's own caching (tokens valid for 1 hour)
    const token = await getIdToken(firebaseUser, false);

    // Cache token for 55 minutes (Firebase tokens are valid for 1 hour)
    // We refresh 5 minutes early to avoid edge cases
    this.token = token;
    this.tokenExpiry = Date.now() + 55 * 60 * 1000; // 55 minutes
    this.lastRefresh = Date.now();

    return token;
  }

  /**
   * Force refresh token (useful after login/logout)
   * @param auth - Firebase auth instance
   * @returns Promise<string> Firebase ID token
   */
  async forceRefresh(auth: Auth): Promise<string> {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      // eslint-disable-next-line no-console
      console.log('🔄 AuthTokenCache: Force refreshing token...');
    }
    this.clearCache();
    return await this.getToken(auth);
  }

  /**
   * Clear cached token
   */
  clearCache(): void {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      // eslint-disable-next-line no-console
      console.log('🗑️ AuthTokenCache: Clearing cache');
    }
    this.token = null;
    this.tokenExpiry = null;
    this.refreshPromise = null;
    this.lastRefresh = null;
  }

  /**
   * Get cache statistics
   * @returns Object with cache stats
   */
  getStats(): {
    hasToken: boolean;
    expiresIn: number | null;
    lastRefresh: string | null;
    isRefreshing: boolean;
  } {
    return {
      hasToken: !!this.token,
      expiresIn: this.tokenExpiry ? Math.round((this.tokenExpiry - Date.now()) / 1000) : null,
      lastRefresh: this.lastRefresh ? new Date(this.lastRefresh).toISOString() : null,
      isRefreshing: !!this.refreshPromise,
    };
  }
}

// Global singleton instance
export const authTokenCache = new AuthTokenCache();

export default authTokenCache;

